import { create } from 'zustand';
import apiClient from '../lib/api';

export type OnboardingStatus = 
  | 'not-initiated' 
  | 'initiated' 
  | 'pending' 
  | 'submitted' 
  | 'under-review' 
  | 'approved' 
  | 'rejected' 
  | 'resend-required' 
  | 'closed' 
  | 'withdrawn';

export interface OnboardingRequest {
  id: string;
  candidateName: string;
  employeeId?: string;
  email: string;
  status: OnboardingStatus;
  currentStep: number;
  joiningDate: string;
  department: string;
  designation: string;
  personalDetails?: any;
  jobDetails?: any;
  complianceDetails?: any;
  bankDetails?: any;
  previousEmployment?: any[];
  familyDetails?: any;
  documents?: any[];
  itChecklist?: any;
  adminChecklist?: any;
  createdAt?: string;
  source?: string;
  /** 'sent' | 'failed' | 'not_configured'; null when no invite was ever sent (e.g. SSO sign-up). */
  inviteStatus?: 'sent' | 'failed' | 'not_configured' | null;
  inviteSentAt?: string | null;
  inviteError?: string | null;
  inviteCount?: number;
}

/** What happened when an invite was sent (POST /onboarding, POST /onboarding/:id/resend-invite). */
export interface InviteOutcome {
  emailed: boolean;
  emailStatus: 'sent' | 'failed' | 'not_configured';
  emailError: string | null;
  /** The invitee already had an account and got an in-app notification. */
  notified: boolean;
}

interface OnboardingState {
  requests: OnboardingRequest[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchRequests: () => Promise<void>;
  /** Creates the request and sends the invite; throws with the server's message on failure. */
  initiateOnboarding: (request: Partial<OnboardingRequest>) => Promise<InviteOutcome>;
  resendInvite: (id: string) => Promise<InviteOutcome>;
  updateRequest: (id: string, data: Partial<OnboardingRequest>) => Promise<void>;
  submitOnboarding: (id: string, data: any) => Promise<void>;
  verifyDocument: (id: string, docId: string, status: 'approved' | 'rejected', comment?: string) => Promise<void>;
  approveRequest: (id: string, data: {
    department: string;
    designation: string;
    roleCode: string;
    employmentType: string;
    reportsTo?: string;
    joiningDate?: string;
    taskTitle?: string;
    taskDescription?: string;
    taskPriority?: string;
    taskDueDate?: string;
  }) => Promise<void>;
  rejectRequest: (id: string, reason?: string) => Promise<void>;
  deleteRequest: (id: string) => Promise<void>;
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  requests: [],
  isLoading: false,
  error: null,

  fetchRequests: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/onboarding');
      set({ requests: (response.data?.data ?? response.data), isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  initiateOnboarding: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post('/people/onboarding', data);
      const { invite, ...request } = response.data?.data ?? response.data;
      set(state => ({ requests: [request, ...state.requests], isLoading: false }));
      return invite as InviteOutcome;
    } catch (error: any) {
      const message = error?.response?.data?.message || error.message || 'Could not start onboarding';
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  resendInvite: async (id) => {
    try {
      const response = await apiClient.post(`/people/onboarding/${id}/resend-invite`);
      const { invite, ...request } = response.data?.data ?? response.data;
      set(state => ({ requests: state.requests.map(r => (r.id === id ? { ...r, ...request } : r)) }));
      return invite as InviteOutcome;
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || error.message || 'Could not resend the invite');
    }
  },

  updateRequest: async (id, data) => {
    try {
      const response = await apiClient.patch(`/people/onboarding/${id}`, data);
      set(state => ({
        requests: state.requests.map(r => r.id === id ? (response.data?.data ?? response.data) : r)
      }));
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  approveRequest: async (id, data) => {
    try {
      const response = await apiClient.post(`/people/onboarding/requests/${id}/approve`, data);
      const updated = response.data?.data ?? response.data;
      set(state => ({
        requests: state.requests.map(r => r.id === id ? updated : r)
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  rejectRequest: async (id, reason) => {
    try {
      const response = await apiClient.post(`/people/onboarding/requests/${id}/reject`, { reason });
      const updated = response.data?.data ?? response.data;
      set(state => ({
        requests: state.requests.map(r => r.id === id ? updated : r)
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  submitOnboarding: async (id, data) => {
    try {
      const response = await apiClient.patch(`/people/onboarding/${id}`, { ...data, status: 'submitted' });
      set(state => ({
        requests: state.requests.map(r => r.id === id ? (response.data?.data ?? response.data) : r)
      }));
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  verifyDocument: async (id, docId, status, comment) => {
    try {
      const request = get().requests.find(r => r.id === id);
      if (!request || !request.documents) return;

      const newDocuments = request.documents.map(doc => 
        doc.id === docId ? { ...doc, status, comment } : doc
      );

      const response = await apiClient.patch(`/people/onboarding/${id}`, { payload: { documents: newDocuments } });
      set(state => ({
        requests: state.requests.map(r => r.id === id ? (response.data?.data ?? response.data) : r)
      }));
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  deleteRequest: async (id) => {
    try {
      await apiClient.delete(`/people/onboarding/${id}`);
      set(state => ({ requests: state.requests.filter(r => r.id !== id) }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  }
}));
